import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { authedFetch, useMe } from '../lib/api';

const WS_URL = import.meta.env.VITE_WS_API_URL ?? '';

// ── Types ────────────────────────────────────────────────────────────────────

interface QuestionData {
  questionIndex: number;
  totalQuestions: number;
  questionId: string;
  text: string;
  options: string[];
  timerSeconds: number;
}

interface RevealData {
  questionIndex: number;
  correctIndex: number;
  briefExplanation: string;
  isQuick?: boolean;
  playerResults: Array<{
    userId: string;
    displayName: string;
    selectedIndex: number | null;
    correct: boolean;
    pointsEarned: number;
    totalPoints: number;
    firstCorrect?: boolean;
  }>;
}

interface LeaderboardEntry {
  userId: string;
  displayName: string;
  totalPoints: number;
  rank: number;
  previousRank: number;
  correctCount: number;
  streak: number;
}

type GamePhase =
  | { type: 'waiting' }
  | { type: 'countdown'; seconds: number; total: number }
  | { type: 'question'; data: QuestionData; deadline: number }
  | { type: 'answered'; data: QuestionData; selected: number; answeredCount: number; totalPlayers: number }
  | { type: 'timeout'; data: QuestionData }
  | { type: 'reveal'; question: QuestionData; reveal: RevealData; timer: number; total: number }
  | { type: 'leaderboard'; entries: LeaderboardEntry[]; questionIndex: number; total: number }
  | { type: 'transition'; seconds: number; nextQuestionIndex: number; total: number; isLast: boolean }
  | { type: 'completed' };

// ── WebSocket hook ───────────────────────────────────────────────────────────

function useGameSocket(
  roomCode: string | undefined,
  onMessage: (msg: any) => void,
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const queueRef = useRef<Record<string, unknown>[]>([]);
  const closedRef = useRef(false); // set on unmount so a closing socket does NOT reconnect
  const [connected, setConnected] = useState(false);

  const connect = useCallback(async () => {
    if (!roomCode || !WS_URL || closedRef.current) return;
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;

      const ws = new WebSocket(`${WS_URL}?room=${roomCode}&token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempt.current = 0;
        setConnected(true);
        // Request the current phase now that the socket is established. (The
        // server can't reliably push during $connect, so the client asks here.)
        try { ws.send(JSON.stringify({ action: 'sync' })); } catch { /* ignore */ }
        // Flush any answer/action buffered during a reconnect.
        const pending = queueRef.current;
        queueRef.current = [];
        for (const p of pending) { try { ws.send(JSON.stringify(p)); } catch { queueRef.current.push(p); } }
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'ping') {
            ws.send(JSON.stringify({ action: 'pong' }));
            return;
          }
          onMessageRef.current(msg);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedRef.current) return; // intentional close on unmount: do NOT reconnect
        const delay = Math.min(30_000, 1000 * Math.pow(2, reconnectAttempt.current));
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => { ws.close(); };
    } catch { /* auth error */ }
  }, [roomCode]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    // Buffer non-pong actions (e.g. answer) to flush on reconnect; the server
    // validates question index so a late replay is harmless.
    if (data.action !== 'pong') queueRef.current.push(data);
    return false;
  }, []);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true; // prevents the post-close reconnect from firing after unmount
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { send, connected };
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function StudyGroupPlayPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const me = useMe();
  const myUserId = me.data?.user.id;

  const [phase, setPhase] = useState<GamePhase>({ type: 'waiting' });
  const [roomCode, setRoomCode] = useState<string | undefined>();
  const [timerPercent, setTimerPercent] = useState(100);
  // Transient "X got it first!" banner (does not block answering).
  const [firstCorrect, setFirstCorrect] = useState<{ userId: string; displayName: string } | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const leaderboardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firstCorrectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hide bottom nav
  useEffect(() => {
    document.body.classList.add('study-group-fullscreen');
    return () => { document.body.classList.remove('study-group-fullscreen'); };
  }, []);

  // Navigation guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Fetch room code from API on mount
  useEffect(() => {
    async function fetchRoomCode() {
      try {
        const data = await authedFetch<{ group: any }>(`/study-groups/${id}`);
        setRoomCode(data.group?.room_code);
      } catch { /* ignore */ }
    }
    fetchRoomCode();
  }, [id]);

  const handleMessage = useCallback((msg: any) => {
    switch (msg.type) {
      case 'countdown': {
        clearTimers();
        setFirstCorrect(null);
        setPhase({ type: 'countdown', seconds: msg.seconds ?? 3, total: msg.totalQuestions ?? 0 });
        startSecondsCountdown('countdown');
        break;
      }
      case 'question': {
        clearTimers();
        setFirstCorrect(null);
        const q: QuestionData = {
          questionIndex: msg.questionIndex,
          totalQuestions: msg.totalQuestions,
          questionId: msg.questionId,
          text: msg.text,
          options: msg.options,
          timerSeconds: msg.timerSeconds,
        };
        const deadline = Date.now() + q.timerSeconds * 1000;
        setPhase({ type: 'question', data: q, deadline });
        startQuestionTimer(q.timerSeconds);
        break;
      }
      case 'answer_ack':
        break;
      case 'answer_received':
        setPhase((prev) => {
          if (prev.type === 'answered') {
            return { ...prev, answeredCount: msg.answeredCount, totalPlayers: msg.totalPlayers };
          }
          return prev;
        });
        break;
      case 'first_correct': {
        // Someone got it right first. Show a transient banner; everyone can still answer.
        setFirstCorrect({ userId: msg.userId, displayName: msg.displayName });
        if (firstCorrectRef.current) clearTimeout(firstCorrectRef.current);
        firstCorrectRef.current = setTimeout(() => setFirstCorrect(null), 4000);
        break;
      }
      case 'reveal': {
        clearTimers();
        setFirstCorrect(null);
        const secs = Math.max(2, msg.revealSeconds ?? 7);
        setPhase((prev) => {
          let question = prev.type === 'question' || prev.type === 'answered' || prev.type === 'timeout'
            ? prev.data
            : undefined;
          // Cold start (connected mid-reveal): build the question from the reveal
          // payload (server includes text/options) so we render instead of bailing.
          if (!question && msg.text) {
            question = {
              questionIndex: msg.questionIndex,
              totalQuestions: msg.totalQuestions ?? 0,
              questionId: msg.questionId ?? '',
              text: msg.text,
              options: msg.options ?? [],
              timerSeconds: 0,
            };
          }
          if (!question) return prev;
          return { type: 'reveal', question, reveal: msg as RevealData, timer: secs, total: secs };
        });
        startRevealTimer(secs);
        break;
      }
      case 'leaderboard':
        clearTimers();
        setPhase({
          type: 'leaderboard',
          entries: msg.entries ?? [],
          questionIndex: msg.questionIndex,
          total: msg.totalQuestions,
        });
        break;
      case 'transition': {
        clearTimers();
        const secs = msg.seconds ?? 3;
        const isLast = !!msg.isLast;
        setPhase({
          type: 'transition',
          seconds: secs,
          nextQuestionIndex: msg.nextQuestionIndex ?? (msg.questionIndex + 1),
          total: msg.totalQuestions,
          isLast,
        });
        startSecondsCountdown('transition');
        if (isLast) {
          // Game over: `completed` will follow, but navigate to results as a
          // fallback so we never get stuck on the final screen if it's delayed.
          leaderboardTimerRef.current = setTimeout(
            () => navigate(`/study-groups/${id}/results`),
            (secs + 4) * 1000,
          );
        }
        break;
      }
      case 'completed':
        clearTimers();
        setPhase({ type: 'completed' });
        setTimeout(() => navigate(`/study-groups/${id}/results`), 1500);
        break;
      case 'reconnected':
        // Legacy safety net (server now sends direct phase messages on connect).
        if (msg.question) handleMessage({ type: 'question', ...msg.question });
        break;
      case 'resync':
        // Server couldn't map the exact phase; show a neutral interstitial so we
        // never sit on the misleading initial "waiting" copy.
        clearTimers();
        setPhase({
          type: 'transition',
          seconds: 0,
          nextQuestionIndex: msg.currentQuestionIndex ?? 0,
          total: msg.totalQuestions ?? 0,
          isLast: false,
        });
        break;
      case 'error':
        break;
    }
  }, [id, navigate]);

  const { send, connected } = useGameSocket(roomCode, handleMessage);

  // Safety net: if we're connected but still on "waiting", re-request a sync, and
  // after a few seconds re-check the group (bail to results if it already
  // finished) so we are never stranded on the waiting screen.
  useEffect(() => {
    if (!connected || phase.type !== 'waiting') return;
    const retry = setTimeout(() => send({ action: 'sync' }), 1500);
    const check = setTimeout(async () => {
      try {
        const data = await authedFetch<{ group: any }>(`/study-groups/${id}`);
        if (data.group?.status === 'completed') navigate(`/study-groups/${id}/results`);
        else send({ action: 'sync' });
      } catch { /* ignore */ }
    }, 6000);
    return () => { clearTimeout(retry); clearTimeout(check); };
  }, [connected, phase.type, id, navigate, send]);

  const startQuestionTimer = (seconds: number) => {
    clearTimers();
    const start = Date.now();
    const duration = seconds * 1000;
    setTimerPercent(100);
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setTimerPercent(remaining);
      if (remaining <= 0) {
        clearTimers();
        setPhase((prev) => {
          if (prev.type === 'question') {
            return { type: 'timeout', data: prev.data };
          }
          return prev;
        });
      }
    }, 50);
  };

  // Drives both the pre-game and between-question 3-2-1 animations. Decrements
  // the `seconds` on the current countdown/transition phase once per second.
  const startSecondsCountdown = (kind: 'countdown' | 'transition') => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setPhase((prev) => {
        if (prev.type !== kind) return prev;
        const next = prev.seconds - 1;
        if (next <= 0 && countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
        return { ...prev, seconds: Math.max(0, next) };
      });
    }, 1000);
  };

  const startRevealTimer = (seconds: number) => {
    let remaining = seconds;
    revealTimerRef.current = setInterval(() => {
      remaining -= 1;
      setPhase((prev) => {
        if (prev.type === 'reveal') {
          return { ...prev, timer: remaining };
        }
        return prev;
      });
      if (remaining <= 0 && revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
      }
    }, 1000);
  };

  const clearTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (revealTimerRef.current) { clearInterval(revealTimerRef.current); revealTimerRef.current = null; }
    if (leaderboardTimerRef.current) { clearTimeout(leaderboardTimerRef.current); leaderboardTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  };

  useEffect(() => () => {
    clearTimers();
    if (firstCorrectRef.current) clearTimeout(firstCorrectRef.current);
  }, []);

  const handleAnswer = (optionIndex: number) => {
    if (phase.type !== 'question') return;
    send({ action: 'answer', questionIndex: phase.data.questionIndex, chosenIndex: optionIndex });
    setPhase({
      type: 'answered',
      data: phase.data,
      selected: optionIndex,
      answeredCount: 1,
      totalPlayers: 1,
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Disconnection overlay
  if (!connected && phase.type !== 'waiting' && phase.type !== 'completed') {
    return (
      <div className="fixed inset-0 z-50 bg-squishy-dark/90 flex flex-col items-center justify-center text-white">
        <div className="animate-spin w-10 h-10 border-3 border-white border-t-transparent rounded-full mb-4" />
        <div className="text-lg font-semibold">Reconnecting...</div>
        <div className="text-sm text-white/60 mt-1">Please wait</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-slate-50 flex flex-col overflow-hidden safe-area-pt safe-area-pb">
      {/* ── First-correct banner (floats over the question) ── */}
      {firstCorrect && (phase.type === 'question' || phase.type === 'answered' || phase.type === 'timeout') && (
        <div className="absolute top-0 inset-x-0 z-30 flex justify-center pointer-events-none px-4 pt-3">
          <div className="bg-squishy-accent text-squishy-dark text-sm font-bold px-4 py-2 rounded-full shadow-lg animate-bounce">
            🏆 {firstCorrect.userId === myUserId ? 'You' : firstCorrect.displayName} got it first!
          </div>
        </div>
      )}

      {/* ── Waiting phase ──────────────────────────────────── */}
      {phase.type === 'waiting' && (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
          <div className="animate-pulse text-lg font-semibold text-squishy-dark mb-2">
            {connected ? 'Syncing with the game...' : 'Connecting...'}
          </div>
          <div className="text-sm">This should only take a moment.</div>
        </div>
      )}

      {/* ── Pre-game countdown (synced 3-2-1) ──────────────── */}
      {phase.type === 'countdown' && (
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-squishy-dark to-slate-900 text-white">
          <div className="text-sm font-semibold uppercase tracking-widest text-white/60 mb-3">Get ready</div>
          <div key={phase.seconds} className="text-8xl font-black animate-pulse">
            {phase.seconds > 0 ? phase.seconds : 'Go!'}
          </div>
          {phase.total > 0 && (
            <div className="text-sm text-white/60 mt-4">{phase.total} questions</div>
          )}
        </div>
      )}

      {/* ── Question phase ─────────────────────────────────── */}
      {(phase.type === 'question' || phase.type === 'answered' || phase.type === 'timeout') && (
        <>
          {/* Top bar */}
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div className="text-xs font-semibold text-slate-500">
              Question {phase.data.questionIndex + 1} of {phase.data.totalQuestions}
            </div>
            <div className="text-xs font-mono text-slate-400">
              {phase.type === 'timeout' ? '0s' : `${Math.ceil((timerPercent / 100) * phase.data.timerSeconds)}s`}
            </div>
          </div>

          {/* Timer bar */}
          <div className="px-4 mb-4">
            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-100 ${
                  timerPercent > 30 ? 'bg-squishy-primary' : timerPercent > 10 ? 'bg-amber-400' : 'bg-red-500'
                }`}
                style={{ width: `${timerPercent}%` }}
              />
            </div>
          </div>

          {/* Question text */}
          <div className="px-4 mb-4 flex-shrink-0 max-h-[42vh] overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-sm leading-relaxed text-slate-800 whitespace-pre-wrap">{phase.data.text}</p>
            </div>
          </div>

          {/* Options */}
          <div className="px-4 space-y-2 flex-1 overflow-y-auto pb-8">
            {phase.data.options.map((opt, i) => {
              const isSelected = phase.type === 'answered' && phase.selected === i;
              const isLocked = phase.type === 'answered' || phase.type === 'timeout';
              return (
                <button
                  key={i}
                  onClick={() => handleAnswer(i)}
                  disabled={isLocked}
                  className={`w-full text-left p-4 rounded-xl border transition-all text-sm ${
                    isSelected
                      ? 'border-squishy-primary bg-squishy-primary/10 text-squishy-dark font-medium'
                      : isLocked
                        ? 'border-slate-200 bg-slate-50 text-slate-400'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-squishy-primary/50 active:scale-[0.98]'
                  }`}
                >
                  <span className="font-semibold mr-2 text-xs">
                    {String.fromCharCode(65 + i)}.
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>

          {/* Status overlays */}
          {phase.type === 'answered' && (
            <div className="px-4 pb-6">
              <div className="bg-squishy-primary/10 text-squishy-primary text-sm font-medium text-center py-3 rounded-xl">
                Waiting for others... ({phase.answeredCount}/{phase.totalPlayers} answered)
              </div>
            </div>
          )}
          {phase.type === 'timeout' && (
            <div className="px-4 pb-6">
              <div className="bg-red-50 text-red-600 text-sm font-semibold text-center py-3 rounded-xl">
                Time's up!
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Reveal phase ───────────────────────────────────── */}
      {phase.type === 'reveal' && (
        <div className="flex-1 flex flex-col px-4 pt-4 pb-8 overflow-y-auto">
          {/* Timer bar */}
          <div className="mb-4">
            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-squishy-accent rounded-full transition-all duration-1000"
                style={{ width: `${(phase.timer / phase.total) * 100}%` }}
              />
            </div>
            <div className="text-right text-xs text-slate-400 mt-1">{phase.timer}s</div>
          </div>

          {/* Question */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-slate-800">{phase.question.text}</p>
          </div>

          {/* Options: green = correct answer, red = your (wrong) pick */}
          <div className="space-y-2 mb-4">
            {(() => {
              const mine = phase.reveal.playerResults?.find((p) => p.userId === myUserId);
              const mySelected = mine && mine.selectedIndex != null ? mine.selectedIndex : null;
              return phase.question.options.map((opt, i) => {
                const isCorrect = i === phase.reveal.correctIndex;
                const isMyWrong = mySelected === i && !isCorrect;
                const cls = isCorrect
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:text-emerald-300 font-medium'
                  : isMyWrong
                    ? 'border-red-400 bg-red-50 text-red-700 dark:text-red-300 font-medium'
                    : 'border-slate-200 bg-slate-50 text-slate-500';
                return (
                  <div key={i} className={`p-3 rounded-xl border text-sm ${cls}`}>
                    <span className="font-semibold mr-2 text-xs">{String.fromCharCode(65 + i)}.</span>
                    {opt}
                    {isCorrect && <span className="ml-2 text-xs font-semibold">✓ Correct answer</span>}
                    {isMyWrong && <span className="ml-2 text-xs font-semibold">✗ Your answer</span>}
                  </div>
                );
              });
            })()}
          </div>

          {/* Explanation (never shown for quick questions) */}
          {!phase.reveal.isQuick && phase.reveal.briefExplanation && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <div className="text-xs font-semibold text-blue-700 mb-1">Explanation</div>
              <p className="text-sm text-blue-800 dark:text-blue-200">{phase.reveal.briefExplanation}</p>
            </div>
          )}

          {/* Who answered what */}
          {phase.reveal.playerResults?.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold text-slate-500">Results</div>
              {phase.reveal.playerResults.map((p) => (
                <div key={p.userId} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <span className={`w-2 h-2 rounded-full ${p.correct ? 'bg-emerald-500' : 'bg-red-400'}`} />
                  <span className="text-sm text-slate-700 flex-1 truncate">
                    {p.userId === myUserId ? 'You' : p.displayName}
                    {p.firstCorrect && <span className="ml-1">🏆</span>}
                  </span>
                  <span className="text-xs text-slate-400">
                    {p.selectedIndex != null ? String.fromCharCode(65 + p.selectedIndex) : '—'}
                  </span>
                  <span className={`text-xs font-semibold ${p.pointsEarned > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    +{p.pointsEarned}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Leaderboard flash ──────────────────────────────── */}
      {phase.type === 'leaderboard' && (
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <h2 className="text-lg font-bold text-squishy-dark mb-4">Standings</h2>
          <div className="w-full max-w-sm space-y-2">
            {phase.entries.slice(0, 8).map((entry) => {
              const rankChange = entry.previousRank - entry.rank;
              return (
                <div
                  key={entry.userId}
                  className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3"
                >
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    entry.rank === 1 ? 'bg-squishy-accent text-squishy-dark' :
                    entry.rank === 2 ? 'bg-slate-300 text-slate-700' :
                    entry.rank === 3 ? 'bg-amber-600/20 text-amber-700' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {entry.rank}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">{entry.displayName}</div>
                    <div className="text-xs text-slate-400">
                      {entry.totalPoints} pts · {entry.streak > 1 ? `${entry.streak} streak` : ''}
                    </div>
                  </div>
                  {rankChange !== 0 && (
                    <div className={`text-xs font-bold ${rankChange > 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                      {rankChange > 0 ? `+${rankChange}` : rankChange}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-xs text-slate-400 mt-4">
            After question {phase.questionIndex + 1} of {phase.total}
          </div>
        </div>
      )}

      {/* ── Transition splash (synced "Next question in 3,2,1") ── */}
      {phase.type === 'transition' && (
        <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-squishy-dark to-slate-900 text-white">
          {phase.isLast ? (
            <>
              <div className="text-2xl font-bold mb-1">Final results</div>
              <div className="mt-2 animate-pulse text-sm text-white/60">Wrapping up...</div>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold uppercase tracking-widest text-white/60 mb-3">Next question in</div>
              <div key={phase.seconds} className="text-8xl font-black animate-pulse">
                {phase.seconds > 0 ? phase.seconds : 'Go!'}
              </div>
              <div className="text-sm text-white/60 mt-4">
                Question {phase.nextQuestionIndex + 1} of {phase.total}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Completed ─────────────────────────────────────── */}
      {phase.type === 'completed' && (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="text-2xl font-bold text-squishy-dark mb-2">Game Over!</div>
          <div className="text-sm text-slate-500">Loading results...</div>
        </div>
      )}
    </div>
  );
}
